import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type {
	PublicBookingInfo,
	PublicBookingAvailability,
	CreatePublicBookingRequest,
	CreatePublicBookingResponse,
	PaidBookingIntentRequest,
	PaidBookingIntentResponse,
	PaidBookingStatus,
} from '../types/publicBooking';

// Every call here is made by a customer who is not signed in, so `skipAuth`
// keeps any portal session that happens to live in this browser out of the
// request.
const PUBLIC = { skipAuth: true } as const;

export const publicBookingApi = {
	/**
	 * Provider, location, services and (for paid links) staff for a booking token.
	 * First call on both booking pages — `paymentRequired` decides which renders.
	 */
	getInfo: async (token: string): Promise<PublicBookingInfo> =>
		apiClient.get<PublicBookingInfo>(API_ENDPOINTS.PUBLIC_BOOKING.INFO(token), PUBLIC),

	/**
	 * Slots for a single date, with existing appointments already accounted for.
	 * @param date `yyyy-MM-dd`
	 * @param options.staffId scopes availability to one stylist (paid links)
	 * @param options.durationMinutes how long the whole basket takes, services
	 * and add-ons together. Without it the server can only tell whether a slot
	 * starts free, so a job too long to finish that day is still offered.
	 * @param options.serviceIds the services in the basket, so that with no
	 * stylist chosen only staff who can do them count towards an open slot.
	 */
	getAvailability: async (
		token: string,
		date: string,
		options: { staffId?: number; durationMinutes?: number; serviceIds?: number[] } = {}
	): Promise<PublicBookingAvailability> => {
		const params = new URLSearchParams({ date });
		if (options.staffId != null) params.append('staffId', String(options.staffId));
		// With no stylist chosen, the server only counts staff skilled for these
		// services when deciding whether a slot is open.
		for (const id of options.serviceIds ?? []) params.append('serviceIds', String(id));
		// A zero or missing duration is nothing to fit a job into, so leave the
		// param off entirely and let the server fall back to its old behaviour.
		if (options.durationMinutes) {
			params.append('durationMinutes', String(options.durationMinutes));
		}
		return apiClient.get<PublicBookingAvailability>(
			`${API_ENDPOINTS.PUBLIC_BOOKING.AVAILABILITY(token)}?${params.toString()}`,
			PUBLIC
		);
	},

	/**
	 * Create the appointment behind a booking token.
	 *
	 * Free links, and waiting-list requests on paid links, only. A timed
	 * booking on a deposit-taking link is refused (400, code 121) because the
	 * appointment must not exist before the deposit is paid — use
	 * `createPaidIntent` → Stripe → `waitForPaidBooking` for those.
	 */
	create: async (
		token: string,
		data: CreatePublicBookingRequest
	): Promise<CreatePublicBookingResponse> =>
		apiClient.post<CreatePublicBookingResponse>(
			API_ENDPOINTS.PUBLIC_BOOKING.CREATE(token),
			data,
			PUBLIC
		),

	/**
	 * Step one of a paid booking: price, hold a PENDING record and get the
	 * PaymentIntent to confirm with Stripe. Nothing is scheduled yet.
	 */
	createPaidIntent: async (
		token: string,
		data: PaidBookingIntentRequest
	): Promise<PaidBookingIntentResponse> =>
		apiClient.post<PaidBookingIntentResponse>(
			API_ENDPOINTS.PUBLIC_BOOKING.PAID_INTENT(token),
			data,
			PUBLIC
		),

	/** Whether the appointment behind a confirmed deposit has been created yet. */
	getPaidStatus: async (token: string, paymentIntentId: string): Promise<PaidBookingStatus> =>
		apiClient.get<PaidBookingStatus>(
			`${API_ENDPOINTS.PUBLIC_BOOKING.PAID_STATUS(token)}?paymentIntentId=${encodeURIComponent(paymentIntentId)}`,
			PUBLIC
		),
};

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Polls `getPaidStatus` until the appointment exists and returns its
 * reference, or null once the attempts run out.
 *
 * Null is not a failure: the deposit has been taken by the time this is
 * called, and the server finalizes the booking from the Stripe webhook whether
 * or not this page is still open. It only means the reference could not be
 * shown yet. Network errors are swallowed for the same reason — a flaky
 * connection after a successful charge must not read as a failed booking.
 */
export const waitForPaidBooking = async (
	token: string,
	paymentIntentId: string,
	{ attempts = 15, intervalMs = 1200 }: { attempts?: number; intervalMs?: number } = {}
): Promise<string | null> => {
	for (let attempt = 0; attempt < attempts; attempt++) {
		try {
			const status = await publicBookingApi.getPaidStatus(token, paymentIntentId);
			if (status.status === 'CONFIRMED' && status.reference) return status.reference;
		} catch {
			// Try again on the next tick.
		}
		await sleep(intervalMs);
	}
	return null;
};
